/**
 * API Client
 *
 * Centralized HTTP client for all API calls to the Boat361 Go backend.
 * Handles request/response serialization, error handling, and authentication.
 */

export interface ApiResponse<T> {
  data?: T;
  error?: {
    message: string;
    code?: string;
  };
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
}

class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor(baseUrl: string = import.meta.env.VITE_API_URL || "http://localhost:8080", timeout: number = 10000) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  /**
   * Get access token from localStorage
   * In production, consider using httpOnly cookies or secure storage
   */
  private getAccessToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        return null;
      }

      const data = await response.json();
      localStorage.setItem("access_token", data.accessToken);
      return data.accessToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      return null;
    }
  }

  /**
   * Make an API request with automatic retry on auth failure
   */
  private async fetchWithAuth<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      headers?: Record<string, string>;
      retryCount?: number;
    } = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    const accessToken = this.getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      let response = await fetch(url, {
        method,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && accessToken && (options.retryCount ?? 0) < 1) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          return this.fetchWithAuth(method, path, { ...options, retryCount: 1 });
        }
        // Refresh failed, token is gone - redirect to login
        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
      }

      if (!response.ok) {
        throw this.parseError(response);
      }

      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Parse error response from API
   */
  private parseError(response: Response): ApiError {
    const error = new Error(`API Error: ${response.statusText}`) as ApiError;
    error.status = response.status;
    error.code = `HTTP_${response.status}`;

    // Try to extract error details from response body
    try {
      const body = response.text();
      if (body) {
        error.details = body;
      }
    } catch {
      // Response body already read or empty
    }

    return error;
  }

  // Public API methods

  async get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("GET", path, { headers });
  }

  async post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("POST", path, { body, headers });
  }

  async patch<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("PATCH", path, { body, headers });
  }

  async put<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("PUT", path, { body, headers });
  }

  async delete<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.fetchWithAuth("DELETE", path, { headers });
  }

  /**
   * Upload file to listing or message
   * Returns signed URL or upload confirmation
   */
  async uploadFile(file: File, path: string): Promise<{ url: string; key: string }> {
    const url = `${this.baseUrl}${path}`;
    const formData = new FormData();
    formData.append("file", file);

    const accessToken = this.getAccessToken();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw this.parseError(response);
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
